/**
 * TypeScript types, constants, and runtime validation for Project Settings.
 *
 * Canonical source of truth:
 *   schemas/project-settings.schema.json
 *
 * Do not add, remove, rename, or retype fields here first. Update the JSON Schema,
 * then keep this module aligned with it. This file is not a second schema.
 */

export const PROJECT_SETTINGS_FILENAME = "project.rudra.config.json"
export const PROJECT_SETTINGS_EXPLORER_PATH = "/project/project.rudra.config.json"
export const PROJECT_SETTINGS_CONFIG_VERSION = "1.0" as const
export const PROJECT_SETTINGS_SCHEMA_ID =
  "https://rudrise.cloud/schemas/project-settings.schema.json"
export const PROJECT_SETTINGS_SCHEMA_PATH = "schemas/project-settings.schema.json"
export const PROJECT_SETTINGS_SCHEMA_HREF = "/schemas/project-settings.schema.json"
export const PROJECT_SETTINGS_HELP_HREF = "/help/project-setting-json-schema"

export type ProjectSettingsConfigVersion = typeof PROJECT_SETTINGS_CONFIG_VERSION

export interface ProjectSettingsProject {
  type: string
  root: string
  mainTexPath: string
}

export interface ProjectSettingsCompiler {
  name: string
  version: string
}

export interface ProjectSettingsLatex {
  compiler: ProjectSettingsCompiler
}

export interface ProjectSettingsSync {
  autoCompile: boolean
  autosave: boolean
}

export interface ProjectSettings {
  configVersion: ProjectSettingsConfigVersion
  project: ProjectSettingsProject
  latex: ProjectSettingsLatex
  sync: ProjectSettingsSync
}

export interface ProjectSettingsValidationError {
  path: string
  message: string
}

export type ProjectSettingsValidationResult =
  | { valid: true; value: ProjectSettings }
  | { valid: false; errors: ProjectSettingsValidationError[] }

/** Current `project.rudra.config.json` shape used by existing projects. */
export const PROJECT_SETTINGS_EXAMPLE: ProjectSettings = {
  configVersion: "1.0",
  project: {
    type: "latex",
    root: ".",
    mainTexPath: "hello-world/main.tex",
  },
  latex: {
    compiler: {
      name: "tectonic",
      version: "0.15.0",
    },
  },
  sync: {
    autoCompile: true,
    autosave: true,
  },
}

export function isProjectSettingsPath(path: string): boolean {
  const name =
    path
      .replace(/\\/g, "/")
      .replace(/\/+$/, "")
      .split("/")
      .filter(Boolean)
      .pop() ?? ""
  return name === PROJECT_SETTINGS_FILENAME
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function error(path: string, message: string): ProjectSettingsValidationError {
  return { path, message }
}

function unexpectedKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
  path: string,
): ProjectSettingsValidationError[] {
  const allowedSet = new Set(allowed)
  return Object.keys(value)
    .filter((key) => !allowedSet.has(key))
    .map((key) =>
      error(
        path ? `${path}.${key}` : key,
        `Unexpected property "${key}" is not allowed by Project Settings configVersion ${PROJECT_SETTINGS_CONFIG_VERSION}.`,
      ),
    )
}

function requireObject(
  value: unknown,
  path: string,
  errors: ProjectSettingsValidationError[],
): Record<string, unknown> | null {
  if (isPlainObject(value)) return value
  errors.push(error(path, `Expected an object, received ${typeName(value)}.`))
  return null
}

function requireString(
  value: unknown,
  path: string,
  errors: ProjectSettingsValidationError[],
): string | null {
  if (typeof value === "string" && value.length > 0) return value
  if (typeof value === "string") {
    errors.push(error(path, "Expected a non-empty string."))
    return null
  }
  errors.push(error(path, `Expected a string, received ${typeName(value)}.`))
  return null
}

function requireBoolean(
  value: unknown,
  path: string,
  errors: ProjectSettingsValidationError[],
): boolean | null {
  if (typeof value === "boolean") return value
  errors.push(error(path, `Expected a boolean, received ${typeName(value)}.`))
  return null
}

function typeName(value: unknown): string {
  if (value === null) return "null"
  if (Array.isArray(value)) return "array"
  return typeof value
}

function requireMissing(
  value: Record<string, unknown>,
  key: string,
  path: string,
  errors: ProjectSettingsValidationError[],
): boolean {
  if (key in value) return false
  errors.push(error(path, `Missing required property "${key}".`))
  return true
}

/**
 * Validate a parsed Project Settings value against the canonical schema rules.
 * Unknown properties are rejected (`additionalProperties: false` at every object).
 */
export function validateProjectSettings(input: unknown): ProjectSettingsValidationResult {
  const errors: ProjectSettingsValidationError[] = []
  const root = requireObject(input, "", errors)
  if (!root) return { valid: false, errors }

  errors.push(
    ...unexpectedKeys(root, ["configVersion", "project", "latex", "sync"], ""),
  )

  requireMissing(root, "configVersion", "configVersion", errors)
  requireMissing(root, "project", "project", errors)
  requireMissing(root, "latex", "latex", errors)
  requireMissing(root, "sync", "sync", errors)

  const configVersion =
    "configVersion" in root ? requireString(root.configVersion, "configVersion", errors) : null
  if (configVersion && configVersion !== PROJECT_SETTINGS_CONFIG_VERSION) {
    errors.push(
      error(
        "configVersion",
        `Unsupported configVersion "${configVersion}". This schema describes format "${PROJECT_SETTINGS_CONFIG_VERSION}".`,
      ),
    )
  }

  let project: ProjectSettingsProject | null = null
  if ("project" in root) {
    const projectObj = requireObject(root.project, "project", errors)
    if (projectObj) {
      errors.push(...unexpectedKeys(projectObj, ["type", "root", "mainTexPath"], "project"))
      requireMissing(projectObj, "type", "project.type", errors)
      requireMissing(projectObj, "root", "project.root", errors)
      requireMissing(projectObj, "mainTexPath", "project.mainTexPath", errors)
      const type = "type" in projectObj ? requireString(projectObj.type, "project.type", errors) : null
      const projectRoot =
        "root" in projectObj ? requireString(projectObj.root, "project.root", errors) : null
      const mainTexPath =
        "mainTexPath" in projectObj
          ? requireString(projectObj.mainTexPath, "project.mainTexPath", errors)
          : null
      if (type && projectRoot && mainTexPath) {
        project = { type, root: projectRoot, mainTexPath }
      }
    }
  }

  let latex: ProjectSettingsLatex | null = null
  if ("latex" in root) {
    const latexObj = requireObject(root.latex, "latex", errors)
    if (latexObj) {
      errors.push(...unexpectedKeys(latexObj, ["compiler"], "latex"))
      requireMissing(latexObj, "compiler", "latex.compiler", errors)
      if ("compiler" in latexObj) {
        const compilerObj = requireObject(latexObj.compiler, "latex.compiler", errors)
        if (compilerObj) {
          errors.push(...unexpectedKeys(compilerObj, ["name", "version"], "latex.compiler"))
          requireMissing(compilerObj, "name", "latex.compiler.name", errors)
          requireMissing(compilerObj, "version", "latex.compiler.version", errors)
          const name =
            "name" in compilerObj
              ? requireString(compilerObj.name, "latex.compiler.name", errors)
              : null
          const version =
            "version" in compilerObj
              ? requireString(compilerObj.version, "latex.compiler.version", errors)
              : null
          if (name && version) {
            latex = { compiler: { name, version } }
          }
        }
      }
    }
  }

  let sync: ProjectSettingsSync | null = null
  if ("sync" in root) {
    const syncObj = requireObject(root.sync, "sync", errors)
    if (syncObj) {
      errors.push(...unexpectedKeys(syncObj, ["autoCompile", "autosave"], "sync"))
      requireMissing(syncObj, "autoCompile", "sync.autoCompile", errors)
      requireMissing(syncObj, "autosave", "sync.autosave", errors)
      const autoCompile =
        "autoCompile" in syncObj
          ? requireBoolean(syncObj.autoCompile, "sync.autoCompile", errors)
          : null
      const autosave =
        "autosave" in syncObj ? requireBoolean(syncObj.autosave, "sync.autosave", errors) : null
      if (autoCompile !== null && autosave !== null) {
        sync = { autoCompile, autosave }
      }
    }
  }

  if (errors.length > 0 || !configVersion || !project || !latex || !sync) {
    return { valid: false, errors }
  }

  return {
    valid: true,
    value: {
      configVersion: PROJECT_SETTINGS_CONFIG_VERSION,
      project,
      latex,
      sync,
    },
  }
}

export function parseProjectSettingsJson(raw: string): ProjectSettingsValidationResult {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw) as unknown
  } catch {
    return {
      valid: false,
      errors: [error("", "Project Settings content is not valid JSON.")],
    }
  }
  return validateProjectSettings(parsed)
}
